/**
 * Typed view of the engine scenario row. Engine returns
 * `ScenarioListItem` from `packages/types/src/scenario-list.ts`. The
 * fields below are the subset the list view renders, with the engine
 * shape in mind:
 *
 *   - `fusionId` is the row's stable identifier (BigInt-as-string).
 *   - `kpiCount` is top-level (NOT nested under `metrics`).
 *   - run metrics are top-level (`lastSuccessfulRunAt` / `*Duration` /
 *     `*Operations`) — there is no `runMetrics` envelope.
 *   - `applications` is `[{ label, type }]` — `type` is the slug we
 *     join against `icon-mapping.json` for the visual icon + colour.
 *   - `workfrontIDs` and `customFields` live on the detail endpoint
 *     only, NOT on the list response — the list-row expand toggle is
 *     therefore gated on the detail fetch, not on the list row.
 */
export interface ScenarioApplication {
  /** Display name shown in the icon tooltip — e.g. "Workfront". */
  label?: string | null;
  /** App slug joined to icon-mapping.json (`data_app`). */
  type?: string | null;
}

export type ScheduleType = 'indefinitely' | 'once' | 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface ScenarioEventSchedule {
  type?: ScheduleType;
  interval?: number;
  date?: string;
  time?: string;
  days?: number[];
  months?: number[];
}

export interface ScenarioRow {
  fusionId: string;
  name: string;
  description?: string | null;
  status?: string | null;
  totalModules?: number | null;
  folderName?: string | null;
  modifiedAt?: string | null;
  modifiedBy?: string | null;
  url?: string | null;
  /** Pointer to the blueprint JSON (read from Fusion API v3) that the
   *  orchestrator uploads for this scenario
   *  (`s3://<blueprints bucket>/{licenseId}/fusion_id_{id}.json`), or
   *  null when no blueprints bucket is configured. The developer-mode
   *  "Download Blueprint" action doesn't read it; it fetches the newest
   *  version through the engine's `/v1/scenarios/{id}/blueprints` and
   *  `/blueprint` routes instead. */
  downloadLink?: string | null;
  /** **Workfront** document version-history URL for the scenario's
   *  blueprint backup. Populated by the `workfront-backup` Fargate
   *  consumer, which uploads each blueprint to a Workfront document
   *  and stores its `/document/{docId}/allVersions` URL on the row.
   *  Used by the developer-mode "Workfront Version Control" action.
   *  Null until the backup workload has run at least once for the
   *  scenario. */
  versionsLink?: string | null;
  applications?: ReadonlyArray<ScenarioApplication>;
  /** Top-level (engine's `ScenarioListItem`). */
  lastSuccessfulRunAt?: string | null;
  /** BigInt-as-string. */
  lastSuccessfulRunDuration?: string | null;
  /** BigInt-as-string. */
  lastSuccessfulRunOperations?: string | null;
  eventSchedule?: ScenarioEventSchedule | null;
  kpiCount?: number;
  /** Optional tags from engine; only rendered in the future. */
  tags?: ReadonlyArray<{ id: number; title: string; color?: string | null; icon?: string | null }>;
  /** Presence flag for the AI technical summary — `true` when the
   *  engine has a non-empty summary for this scenario. Used to gate
   *  the Sparkles/wrench icon on the row. The full markdown is no
   *  longer shipped on the list response (it was the single biggest
   *  contributor to payload size); the AI Summary sheet fetches it
   *  on open from `/v1/scenarios/{id}`. Demo mode keeps the full
   *  text on `aiTechnicalSummary` below — the gate accepts either
   *  signal. */
  hasAiTechnicalSummary?: boolean;
  /** Full AI-generated technical summary text. **No longer ships
   *  on the engine's list response** — present in demo mode (fixture
   *  pre-populates it) and in scenario-detail responses. The
   *  scenario-list path should consult `hasAiTechnicalSummary` for
   *  gating + lazy-fetch the full text from the detail endpoint
   *  when the operator opens the AI Summary sheet. */
  aiTechnicalSummary?: string | null;
  /** AI-generated business summary; rendered in the Description
   *  column (line-clamped) when present, falling back to the raw
   *  Fusion `description`. Mirrors the legacy `wf-audit/src/slices/
   *  scenario/components/scenario/DefaultTable.tsx` Description
   *  cell — business summary lives in the list column, technical
   *  summary lives behind the per-row Sparkles action. */
  aiBusinessSummary?: string | null;
  /** Fusion team id (BigInt-as-string). Surfaced by the engine's
   *  detail endpoint, sometimes on the list response too. */
  teamFusionId?: string | null;
  /** Fusion screenshot URL — populated on detail responses; renders
   *  as the process-diagram thumbnail inside the detail modal. */
  screenshotLink?: string | null;
  /** Operator-set hourly rate ($) — feeds the Value Metrics ROI
   *  cell `value = hoursSaved × hourlyRate × successCount`. Stored
   *  as `NUMERIC(12,2)` in Postgres and string-encoded on the wire
   *  to preserve cents precision. Edited from the Value Metrics
   *  page's per-row inputs (revealed by the `Value` column's edit
   *  pencil). */
  hourlyRate?: string | null;
  /** Hours-saved-per-execution multiplier — see `hourlyRate` for
   *  the matching ROI formula. */
  hoursSaved?: string | null;
}

export type ScenarioStatusFilter = 'all' | 'active' | 'inactive';

export type ScenarioSortField = 'name' | 'description' | 'modifiedAt' | 'applications';
export type ScenarioSortDirection = 'asc' | 'desc';

/**
 * Cast a loosely-typed engine row to the narrowed view we render.
 */
export function asScenarioRow(row: Record<string, unknown>): ScenarioRow {
  return row as unknown as ScenarioRow;
}

/**
 * Status helper. Engine returns the legacy `'On' | 'Off'` strings; the
 * UI shows `Active` / `Inactive` badges. Anything else is treated as
 * inactive so unknown enum values default to the safer rendering.
 */
export function isActive(row: ScenarioRow): boolean {
  return row.status === 'On';
}
